import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Divider,
	FormControl,
	FormControlLabel,
	FormLabel,
	InputLabel,
	Menu,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Tree from "rc-tree";
import TPCheckbox from "../../TemplateProperties/TPCheckbox";
import { cloneDeep, debounce, isEmpty, set } from "lodash";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { scrollToSelectedItem } from "../../../utils/utilFunctions";
import SourceType from "../../TemplateProperties/SourceType";
import {
	fieldTypeOptionsArray,
	operatorsDropdowns,
} from "../../../constants/constantList";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "../../../pages/workbench_build/WorkbenchBuild";
import { treeActions } from "../../../redux/slices/treeSlice";
import { eachDeep } from "deepdash-es/standalone";
import { SectionHeader } from "./SectionHeader";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DatePattern from "../PatternModal/DatePattern";
import { lgDocPath } from "../../../constants/languageDocPaths";
import { api } from "../../../redux/api";
import LightTooltip from "../../../commonComponents/LightTooltip";

const fieldTypeOptions = [
	...fieldTypeOptionsArray,
	{ value: "CONSTANT", text: "CONSTANT" },
];

const conditionNode = {
	COMPARE: "EQUALS",
	COMPARE_ACTION_IGNORECASE: "FALSE",
	COMPARE_ACTION_NOT: "FALSE",
	COMPARE_ACTION_TRIM: "FALSE",
	NAME: `UNNAMED`,
	FIELDTYPE: "ALPHA",
};

const compoundConditionNode = {
	NAME: "COMPOUND CONDITION",
	CHILDOPERATOR: "AND",
	CONDITION: [
		{
			COMPARE: "EQUALS",
			COMPARE_ACTION_IGNORECASE: "FALSE",
			COMPARE_ACTION_NOT: "FALSE",
			COMPARE_ACTION_TRIM: "FALSE",
			NAME: `UNNAMED1`,
			OPERATOR: "AND",
			FIELDTYPE: "ALPHA",
		},
		{
			COMPARE: "EQUALS",
			COMPARE_ACTION_IGNORECASE: "FALSE",
			COMPARE_ACTION_NOT: "FALSE",
			COMPARE_ACTION_TRIM: "FALSE",
			NAME: `UNNAMED2`,
			OPERATOR: "AND",
			FIELDTYPE: "ALPHA",
		},
	],
};

const ConditionalIfFields = () => {
	const { t } = useTranslation();
	const [treeData, setTreeData] = useState([]);
	const [selectedKey, setSelectedKey] = useState([]);
	const [expandedKeys, setExpandedKeys] = useState([]);
	const [selectedNode, setSelectedNode] = useState({});
	const [anchorEl, setAnchorEl] = useState(null);
	const [conditionalFieldName, setConditionalFieldName] = useState("");
	const [compareValue, setCompareValue] = useState("EMPTY");
	const [notValue, setNotValue] = useState("FALSE");
	const [ignoreCaseValue, setIgnoreCaseValue] = useState("FALSE");
	const [trimValue, setTrimValue] = useState("FALSE");
	const [conditionName, setConditionName] = useState("");
	const [childOperatorValue, setChildOperatorValue] = useState("OR");
	const parentScreen = useContext(ParentScreenCtx);
	const [datePatternModal, setDatePatternModal] = useState(false);
	const [pattern, setPattern] = useState("");
	const [datePatternModal2, setDatePatternModal2] = useState(false);
	const [pattern2, setPattern2] = useState("");
	const [patternValid, setPatternValid] = useState(true);
	const [fieldType, setFieldType] = useState("SELECT");
	const [valid, setValid] = useState(true);
	const [dpNetworkCallMessage, setDpNetworkCallMessage] = useState("");
	const [tempPattern, setTempPattern] = useState("");
	const [constantValue, setConstantValue] = useState("");
	const [isDateMapped, setIsDateMapped] = useState(false);
	const { rightTree, leftTree } = useSelector(
		(state) => state.treeData[parentScreen]
	);
	const rightTreeSelectedNode =
		rightTree.tree[rightTree.currentTab].selectedNode;
	const { baseUrl, lang } = useSelector((state) => state.queryParams);
	const conditionRef = useRef();
	const dispatch = useDispatch();
	const docPath = `${baseUrl}/docs/${
		lgDocPath[lang] || "en"
	}/Workbench/dialogs/DateFormatter.html`;

	const operators = useMemo(
		() =>
			[...operatorsDropdowns, {value: "BYTE", text: t("BYTE")}]?.map((obj, key) => (
				<MenuItem key={key} value={obj.value}>
					{obj.text}
				</MenuItem>
			)),
		[operatorsDropdowns]
	);

	const fieldTypeDropdown = useMemo(
		() =>
			fieldTypeOptions?.map((obj, key) => (
				<MenuItem key={key} value={obj.value}>
					{t(obj.text)}
				</MenuItem>
			)),
		[operatorsDropdowns]
	);

	const dataToRCTree = (data) => {
		const clonedData = JSON.parse(JSON.stringify(data));
		const loopData = (data, parentKey) => {
			return data?.map((e, i) => {
				if (e.CONDITION) {
					e.ICON_DISPLAY = "COMPOUND_CONDITION";
				} else {
					e.ICON_DISPLAY = "MAPPING_CONDITION";
				}
				e["key"] = parentKey + "-" + i;
				if (e?.CONDITION && e?.CHILDOPERATOR)
					e.title = e?.NAME + ` (${e.CHILDOPERATOR})`;
				else e.title = e?.NAME;
				if (e?.CONDITION?.length)
					e.children = loopData(e?.CONDITION, parentKey + "-" + i);
				else e.children = [];
				return e;
			});
		};

		const newData = loopData(clonedData, "cf");
		setTreeData(newData);
	};

	const handleMenuClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = (source) => {
		setAnchorEl(null);
		if (source) {
			const data = JSON.parse(JSON.stringify(treeData));
			const newNode =
				source === "CONDITION" ? conditionNode : compoundConditionNode;
			if (!selectedKey?.length) {
				data.push(newNode);
				setSelectedKey(["cf-0"]);
				setSelectedNode({ key: "cf-0", ...newNode });
				if (source === "COMPOUND_CONDITION") setExpandedKeys(["cf-0"]);
			} else {
				const keyArray = selectedKey?.[0]?.split("-") || [];
				const [pre, ...rest] = keyArray;
				let tempData = data;
				for (let i = 0; i < rest.length; i++) {
					const index = Number(rest[i]);
					if (i === rest.length - 1) {
						if (tempData?.[index]?.CHILDOPERATOR && source === "CONDITION") {
							tempData = tempData?.[index]?.CONDITION;
							tempData.push(newNode);
							const key = selectedKey[0] + "-" + `${tempData?.length - 1}`;
							setSelectedKey([key]);
							setSelectedNode({ key, ...newNode });
							setExpandedKeys((prev) => [...prev, selectedKey[0]]);
						} else {
							tempData.push(newNode);
							keyArray.pop();
							keyArray.push(tempData.length - 1);
							setSelectedKey([keyArray?.join("-")]);
							setSelectedNode({ key: keyArray?.join("-"), ...newNode });
							if (source === "COMPOUND_CONDITION")
								setExpandedKeys((prev) => [...prev, keyArray?.join("-")]);
						}
					} else {
						tempData = tempData?.[index]?.CONDITION;
					}
				}
			}
			dataToRCTree(data);
			submitHandler("CONDITIONS", data);
		}
	};

	const conditionDeleteHandler = () => {
		const data = JSON.parse(JSON.stringify(treeData));
		const keyArray = selectedKey?.[0]?.split("-") || [];
		const [pre, ...rest] = keyArray;
		let tempData = data;
		let parentNode = {};
		for (let i = 0; i < rest.length; i++) {
			const index = Number(rest[i]);
			if (i === rest.length - 1) {
				let key = selectedKey?.[0]?.split("-");
				if (index === 0) {
					if (key?.length === 2 && tempData?.length === 1) {
						setSelectedKey([]);
						setSelectedNode({});
					}
					if (key?.length === 2 && tempData?.length > 1) {
						setSelectedKey(["cf-0"]);
						setSelectedNode(tempData?.[1] || {});
					} else {
						if (key.length > 2) {
							key.pop();
							setSelectedKey([key.join("-")]);
							setSelectedNode(parentNode);
						} else {
							setSelectedKey([]);
							setSelectedNode({});
						}
					}
				} else {
					key?.pop();
					key?.push(index - 1);
					setSelectedKey([key?.join("-")]);
					setSelectedNode(tempData?.[index - 1] || {});
				}
				tempData.splice(index, 1);
			} else {
				parentNode = tempData?.[index];
				tempData = tempData?.[index]?.CONDITION;
			}
		}
		dataToRCTree(data);
		submitHandler("CONDITIONS", data);
	};

	const conditionCopyHandler = () => {
		const data = cloneDeep(treeData);
		const keyArray = selectedKey?.[0]?.split("-") || [];
		const [pre, ...rest] = keyArray;
		let tempData = data;
		for (let i = 0; i < rest.length; i++) {
			const index = Number(rest[i]);
			if (i === rest.length - 1) {
				let node = tempData?.[index];
				node = {
					...node,
					NAME: `${t("FRG_COPY_OF_")} ${node?.NAME}`,
				};
				tempData.push(node);
				let key = selectedKey?.[0]?.split("-");
				key.pop();
				key.push(tempData?.length - 1);
				key = key.join("-");
				setSelectedKey([key]);
				setSelectedNode({ ...node, key });
				if (node?.CHILDOPERATOR) setExpandedKeys((prev) => [...prev, key]);
			} else {
				tempData = tempData?.[index]?.CONDITION;
			}
		}
		dataToRCTree(data);
		submitHandler("CONDITIONS", data);
	};

	const Icon = (node) => {
		return (
			<span
				data-key={`${node?.key}`}
				className={"tree-icon " + node.ICON_DISPLAY}
			></span>
		);
	};

	const switcherIcon = (obj) => {
		if (obj.isLeaf) return;
		if (obj.expanded) return <ArrowDropDownIcon />;
		else return <ArrowRightIcon sx={{ color: "#666666" }} />;
	};

	const titleRender = (node) => {
		return <span data-key={`${node?.key}`}>{node.title}</span>;
	};

	const selectHandler = (keys, info) => {
		setSelectedKey([info?.node?.key]);
		setSelectedNode(info.node);
	};

	const expandHandler = (keys, info) => {
		setExpandedKeys(keys);
	};

	useEffect(() => {
		if (conditionRef?.current) {
			scrollToSelectedItem(selectedKey, conditionRef);
		}
	}, [selectedKey]);

	useEffect(() => {
		const conditions = rightTreeSelectedNode?.CONDITIONLIST?.CONDITION || [];
		setConditionalFieldName(rightTreeSelectedNode?.NAME);
		setSelectedKey(conditions?.length ? ["cf-0"] : []);
		setSelectedNode(
			conditions?.length
				? { ...(conditions?.[0] || {}), key: "cf-0" } || {}
				: {}
		);
		dataToRCTree(conditions);
	}, [rightTreeSelectedNode?.key, rightTree?.currentTab]);

	useEffect(() => {
		setConditionName(selectedNode?.NAME || "");
		setCompareValue(
			operatorsDropdowns.filter((e) => e?.value === selectedNode?.COMPARE)
				?.length
				? selectedNode?.COMPARE
				: "EMPTY"
		);
		setIgnoreCaseValue(selectedNode?.COMPARE_ACTION_IGNORECASE || "FALSE");
		setNotValue(selectedNode?.COMPARE_ACTION_NOT || "FALSE");
		setTrimValue(selectedNode?.COMPARE_ACTION_TRIM || "FALSE");
		setChildOperatorValue(selectedNode?.CHILDOPERATOR || "OR");
		setPattern(selectedNode?.PATTERN || "");
    	setPattern2(selectedNode?.INBOUND_PATTERN_2 || "");
		setFieldType(selectedNode?.FIELDTYPE || "SELECT");
		setConstantValue(selectedNode?.VALUE || "");
  
	}, [selectedNode?.key, selectedNode]);

	const cleanData = (data) => {
		const newData = JSON.parse(JSON.stringify(data));
		newData.forEach((e) => {
			Object.keys(e).forEach((key) => {
				if (e[key] === undefined || e[key] === null || key.toLowerCase() === key) {
					delete e[key];
				}
			});
		});
		return newData;
	};

	const submitHandler = (key, value, valuesObj) => {
		const treeData = cloneDeep(
			rightTree?.tree?.[rightTree?.currentTab].translatedData
		);
		let found = false;
		eachDeep(treeData, (obj) => {
			if (found) return;
			if (obj?.key && obj?.key === rightTreeSelectedNode?.key) {
				found = true;
				if (!key) {
					Object.keys(valuesObj).forEach((e) => {
						obj[e] = valuesObj[e];
					});
				} else if (key === "CONDITIONS")
					obj["CONDITIONLIST"]["CONDITION"] = cleanData(value);
				else obj[key] = value;
				if (key === "NAME") obj.title = t("CONDITIONAL_FIELDS_") + value;
				dispatch(
					treeActions.setRightTreeSelectedNode({
						screen: parentScreen,
						type: rightTree.currentTab,
						data: obj,
					})
				);
			}
		});
		dispatch(
			treeActions.setRightTreeTransData({
				screen: parentScreen,
				type: rightTree.currentTab,
				data: treeData,
			})
		);
	};

	const conditionsUpdater = (key, value, valuesObj) => {
		const rcTreeData = cloneDeep(treeData);
		eachDeep(rcTreeData, (obj) => {
			if (obj?.key && obj?.key === selectedNode?.key) {
				if (!key) {
					Object.keys(valuesObj).forEach((e) => {
						obj[e] = valuesObj[e];
					});
				} else obj[key] = value;
			}
			["key", "title", "ICON_DISPLAY"].forEach((e) => {
				if (obj?.hasOwnProperty(e)) delete obj?.[e];
			});
		});
		dataToRCTree(rcTreeData);
		submitHandler("CONDITIONS", rcTreeData);
	};

	const mapping1Handler = (values) => {
		debounceConditionHandler(null, null, {
			PARENT: values.PARENT,
			RECORD_TYPE: values.RECORD_TYPE,
			MAPNAME: values.MAPNAME,
			ACTION1: values?.ACTION,
			INDEX1: values?.INDEX,
			CUSTOM_DB_FIELD: values?.CUSTOM_DB_FIELD,
			PLUGIN_NAME: values?.PLUGIN_NAME,
			MAP_REPETITION_INDEX: values?.MAP_REPETITION_INDEX,
			SOURCE_REPEATING_FIELD: values?.SOURCE_REPEATING_FIELD,
		});
	};

	const mapping2Handler = (values) => {
		setIsDateMapped(values?.FIELDTYPE === "DATE" || ["TODAY", "TOMORROW", "YESTERDAY"].includes(values?.MAPNAME));
		setPattern2(values?.PATTERN || "");
		debounceConditionHandler(null, null, {
			PARENT2: values.PARENT,
			RECORD_TYPE2: values.RECORD_TYPE,
			MAPNAME2: values.MAPNAME,
			ACTION2: values?.ACTION,
			INDEX2: values?.INDEX,
			CUSTOM_DB_FIELD2: values?.CUSTOM_DB_FIELD,
			PLUGIN_NAME2: values?.PLUGIN_NAME,
			INBOUND_PATTERN_2: values?.PATTERN || undefined,
			MAP_REPETITION_INDEX_2: values?.MAP_REPETITION_INDEX,
			SOURCE_REPEATING_FIELD_2: values?.SOURCE_REPEATING_FIELD,
		});
	};

	const debounceChangeHandler = useCallback(debounce(submitHandler, 300), [
		rightTree.currentTab,
		rightTree.tree,
		rightTree.tree[rightTree.currentTab].translatedData,
	]);

	const debounceConditionHandler = useCallback(
		debounce(conditionsUpdater, 300),
		[
			rightTree.currentTab,
			rightTree.tree,
			rightTree.tree[rightTree.currentTab].translatedData,
			selectedNode,
			treeData,
		]
	);

	const testDateHandler = (key, val) => {
		if (!val) {
			setDpNetworkCallMessage(
				t(
					"BOX_NO_FORMAT_WAS_SPECIFIED__SEL_CANCEL_TO_CLS_DIALOG_AND_RET_TO_MAIN_SCREEN"
				)
			);
			setValid(false);
		}
		if (val) {
			dispatch(
				api.endpoints.validateDatePattern.initiate({
					datePattern: encodeURIComponent(val),
				})
			).then((response) => {
				if (response) {
					if (response.data.current_state === "ERROR") {
						setPatternValid(false);
						setValid(false);
						setDpNetworkCallMessage(response.data.message);
					} else {
						setDpNetworkCallMessage(response.data.message)
						setValid(true);
					}
				}
			});
		}
	};

	return (
		<Box sx={{ padding: "15px 15px" }}>
			<Stack spacing={"15px"}>
				<FormControl className="width-100" size="small">
					<TextField
						variant="outlined"
						size="small"
						label={t("LBL_COND_FLD_LIST_NAME")}
						className={"width-100"}
						value={conditionalFieldName}
						onBlur={() =>
							setConditionalFieldName(
								conditionalFieldName ? conditionalFieldName : "UNNAMED"
							)
						}
						onChange={(e) => {
							const value = e?.target?.value ? e?.target?.value : "UNNAMED";
							setConditionalFieldName(e?.target?.value || "");
							debounceChangeHandler("NAME", value);
						}}
					/>
				</FormControl>
				<div
					style={{
						marginBottom: "4px",
						overflowX: "hidden",
						position: "relative",
					}}
					ref={conditionRef}
					className="div-textBox-conditional-field"
				>
					<div
						style={{
							backgroundColor: "#F2F2F2",
							position: "sticky",
							top: "0",
							zIndex: "1000",
						}}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								width: "93%",
								margin: "auto",
								padding: "2px",
							}}
						>
							<div
								style={{
									color: "#666666",
									fontSize: "13px",
									fontWeight: "500",
								}}
							>
								{t("LBL_CONDITIONS")}
							</div>
							<div style={{ display: "flex", gap: "5px" }}>
								<div>
									<LightTooltip arrow onClick={handleMenuClick}>
										<span
											style={{ cursor: "pointer" }}
											className="ADD_MUI"
										></span>
									</LightTooltip>
									<Menu
										anchorEl={anchorEl}
										open={Boolean(anchorEl)}
										onClose={() => handleMenuClose()}
										anchorOrigin={{
											vertical: "bottom",
											horizontal: "left",
										}}
									>
										<MenuItem onClick={() => handleMenuClose("CONDITION")}>
											{t("MNU_NEW_COND")}
										</MenuItem>
										<MenuItem
											onClick={() => handleMenuClose("COMPOUND_CONDITION")}
										>
											{t("MNU_NEW_COMP_COND")}
										</MenuItem>
									</Menu>
								</div>
								<LightTooltip
									onClick={conditionDeleteHandler}
									title={t("DELETE")}
									arrow
								>
									<span
										style={{ cursor: "pointer" }}
										className="DELETE_MUI"
									></span>
								</LightTooltip>
								<LightTooltip
									onClick={conditionCopyHandler}
									title={t("MNU_COPY")}
									arrow
								>
									<span
										style={{ cursor: "pointer" }}
										className="FILE_COPY"
									></span>
								</LightTooltip>
							</div>
						</div>
					</div>
					<Tree
						titleRender={titleRender}
						treeData={treeData}
						icon={Icon}
						selectedKeys={selectedKey}
						switcherIcon={switcherIcon}
						onSelect={selectHandler}
						expandedKeys={expandedKeys}
						onExpand={expandHandler}
					/>
				</div>
				<FormControl className="width-100" size="small">
					<TextField
						disabled={!selectedNode?.key}
						variant="outlined"
						size="small"
						label={t("LBL_CONDITION_NAME")}
						className={"width-100"}
						value={conditionName}
						onBlur={() =>
							setConditionName(conditionName ? conditionName : "UNNAMED")
						}
						onChange={(e) => {
							const name = e?.target?.value ? e?.target?.value : "UNNAMED";
							setConditionName(e?.target?.value);
							debounceConditionHandler("NAME", name);
						}}
					/>
				</FormControl>
				{selectedNode?.CONDITION ? (
					<div>
						<FormLabel variant="body2">
							{t("LBL_SEL_OPER_FOR_COND_CONTAINED_WITHIN_COMP_COND")}
						</FormLabel>
						<FormControl
							sx={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
							}}
							ml={3}
						>
							<FormLabel>{t("LBL_COMPOUND_OPER")}</FormLabel>
							<RadioGroup
								row
								name="CHILDOPERATOR"
								value={childOperatorValue}
								onChange={(e) => {
									setChildOperatorValue(e.target.value);
									debounceConditionHandler("CHILDOPERATOR", e.target.value);
								}}
							>
								<FormControlLabel
									value={"OR"}
									control={<Radio />}
									label={"OR"}
								/>
								<FormControlLabel
									value={"AND"}
									control={<Radio />}
									label={"AND"}
								/>
							</RadioGroup>
						</FormControl>
					</div>
				) : (
					<>
						<Accordion sx={styles.accordianContainer} defaultExpanded>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								aria-controls="accordianFormattingOptions"
								id="accordianFormattingOptions"
								className="accordian-override"
								sx={styles.accordianHeader}
							>
								<SectionHeader text={`${t("LBL_MAPPING")} 1`} />
							</AccordionSummary>
							<AccordionDetails sx={styles.accordianContent}>
								<Stack spacing={"10px"} marginTop={"10px"}>
									<SourceType
										node={{
											PARENT: selectedNode?.PARENT,
											MAPNAME: selectedNode?.MAPNAME,
											RECORD_TYPE: selectedNode?.RECORD_TYPE,
											ACTION: selectedNode?.ACTION1,
											INDEX: selectedNode?.INDEX1,
											CUSTOM_DB_FIELD: selectedNode?.CUSTOM_DB_FIELD,
											PLUGIN_NAME: selectedNode?.PLUGIN_NAME,
											key: selectedNode?.key,
											MAP_REPETITION_INDEX: selectedNode?.MAP_REPETITION_INDEX,
									
										}}
										disabled={!selectedNode?.key}
										valuesHandler={mapping1Handler}
									/>
								</Stack>
							</AccordionDetails>
						</Accordion>
						<>
							<Divider />
							<FormControl
								variant="outlined"
								className="width-100"
								size="small"
							>
								<InputLabel
									sx={isEmpty(selectedNode) ? {} : {}}
									id="operatorDropdown"
								>
									{t("LBL_OPERATOR")}
								</InputLabel>
								<Select
									disabled={!selectedNode?.key}
									className={"width-100"}
									MenuProps={{
										PaperProps: {
											sx: {
												maxHeight: 300,
											},
										},
									}}
									name={"COMPARE"}
									value={compareValue}
									labelId="operatorDropdown"
									label={t("LBL_OPERATOR")}
									id="operatorDropdown"
									onChange={(e) => {
										setCompareValue(e.target.value);
										debounceConditionHandler(null, null, {
											COMPARE: e.target.value,
											...(e.target.value === "REGULAR_EXPRESSION" ? {FIELDTYPE: "CONSTANT", RECORD_TYPE2: undefined, MAPNAME2: undefined} : {}),
										});
										if (e.target.value === "REGULAR_EXPRESSION") {
											setFieldType("CONSTANT");
											setSelectedNode({});
											setTimeout(() => {
												setSelectedNode({
													...selectedNode,
													FIELDTYPE: "CONSTANT",
													RECORD_TYPE2: undefined,
													MAPNAME2: undefined,
												});
											}, 0);
										}
									}}
								>
									{operators}
								</Select>
							</FormControl>
							<div className="flex" style={{ marginLeft: "2px" }}>
								<TPCheckbox
									disabled={isEmpty(selectedNode)}
									name="COMPARE_ACTION_NOT"
									checked={notValue === "TRUE"}
									label={t("BTN_NOT")}
									handleCheck={(checked) => {
										setNotValue(checked ? "TRUE" : "FALSE");
										debounceConditionHandler(
											"COMPARE_ACTION_NOT",
											checked ? "TRUE" : "FALSE"
										);
									}}
								/>
								<TPCheckbox
									disabled={isEmpty(selectedNode)}
									name={"COMPARE_ACTION_IGNORECASE"}
									label={t("IGNORECASE")}
									checked={ignoreCaseValue === "TRUE"}
									handleCheck={(checked) => {
										setIgnoreCaseValue(checked ? "TRUE" : "FALSE");
										debounceConditionHandler(
											"COMPARE_ACTION_IGNORECASE",
											checked ? "TRUE" : "FALSE"
										);
									}}
								/>
								<TPCheckbox
									disabled={isEmpty(selectedNode)}
									name={"COMPARE_ACTION_TRIM"}
									label={t("BTN_TRIM_DATA")}
									checked={trimValue === "TRUE"}
									handleCheck={(checked) => {
										setTrimValue(checked ? "TRUE" : "FALSE");
										debounceConditionHandler(
											"COMPARE_ACTION_TRIM",
											checked ? "TRUE" : "FALSE"
										);
									}}
								/>
							</div>
							{compareValue === "IS_DATE" && (
								<FormControl className="width-100" size="small">
									<div className="flex">
										<TextField
											variant="outlined"
											size="small"
											label={t("LBL_PATTERN")}
											className={"width-100"}
											name="PATTERN"
											value={pattern}
											// error={!valid}
											onChange={(e) => {
												setPattern(e.target.value);
												debounceConditionHandler("PATTERN", e.target.value);
											}}
										/>
										<Button
											variant="outlined"
											size="small"
											disableElevation
											className="margin-left-4"
											sx={{
												width: "40px",
												padding: "4px 8px",
												minWidth: "40px",
											}}
											onClick={() => {
												setDatePatternModal(true);
											}}
										>
											<CalendarMonthOutlinedIcon />
										</Button>
									</div>
								</FormControl>
							)}
						</>
						{!["EMPTY", "IS_DATE", "IS_NUMERIC", "FALSE", "TRUE"].includes(
							compareValue
						) && (
							<Accordion sx={styles.accordianContainer} defaultExpanded>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									aria-controls="accordianFormattingOptions"
									id="accordianFormattingOptions"
									className="accordian-override"
									sx={styles.accordianHeader}
								>
									<SectionHeader text={`${t("LBL_MAPPING")} 2`} />
								</AccordionSummary>
								<AccordionDetails sx={styles.accordianContent}>
									<Stack spacing={"10px"} marginTop={"10px"}>
										<FormControl
											variant="outlined"
											className="width-100"
											size="small"
										>
											<InputLabel
												sx={isEmpty(selectedNode) ? {} : {}}
												id="operatorDropdown"
											>
												{t("LBL_FIELD_TYPE")}
											</InputLabel>
											<Select
												disabled={!selectedNode?.key}
												variant="outlined"
												size="small"
												className={"width-100"}
												value={!selectedNode?.key ? "" : fieldType}
												onChange={(e) => {
													setFieldType(e.target.value);
													debounceConditionHandler(null, null, {
														FIELDTYPE: e.target.value,
														...(e.target.value === "CONSTANT"
															? {
																	RECORD_TYPE2: "CONSTANT",
																	MAPNAME2: undefined,
																	ACTION2: undefined,
																	INDEX2: undefined,
																	CUSTOM_DB_FIELD2: undefined,
																	PLUGIN_NAME2: undefined,
															  }
															: {}),
													});
												}}
											>
												{fieldTypeDropdown}
											</Select>
										</FormControl>
										{fieldType !== "CONSTANT" && <SourceType
											disabled={!selectedNode?.key}
											node={{
												PARENT: selectedNode?.PARENT2,
												MAPNAME: selectedNode?.MAPNAME2,
												RECORD_TYPE: selectedNode?.RECORD_TYPE2,
												ACTION: selectedNode?.ACTION2,
												INDEX: selectedNode?.INDEX2,
												CUSTOM_DB_FIELD: selectedNode?.CUSTOM_DB_FIELD2,
												PLUGIN_NAME: selectedNode?.PLUGIN_NAME2,
												key: selectedNode?.key,
												MAP_REPETITION_INDEX: selectedNode?.MAP_REPETITION_INDEX_2,
											}}
											initialSelectCall={true}
											valuesHandler={mapping2Handler}
										/>}
										{fieldType === "CONSTANT" ? (
											<FormControl className="width-100" size="small">
												<TextField
													disabled={!selectedNode?.key}
													variant="outlined"
													size="small"
													label={t("CONSTANT_VALUE")}
													className={"width-100"}
													value={constantValue}
													onChange={(e) => {
														setConstantValue(e?.target?.value);
														debounceConditionHandler("VALUE", e?.target?.value);
													}}
												/>
											</FormControl>
										) : fieldType === "DATE" ? (
											<FormControl className="width-100" size="small">
												<div className="flex">
													<TextField
														variant="outlined"
														size="small"
														label={t("LBL_INBOUND_DATE_PATTERN")}
														className={"width-100"}
														name="PATTERN"
														value={pattern2}
														disabled={isDateMapped}
														onChange={(e) => {
															setPattern2(e.target.value);
															debounceConditionHandler(
																"INBOUND_PATTERN_2",
																e.target.value
															);
														}}
													/>
													<Button
														disabled={isDateMapped}
														variant="outlined"
														size="small"
														disableElevation
														className="margin-left-4"
														sx={{
															width: "40px",
															padding: "4px 8px",
															minWidth: "40px",
														}}
														onClick={() => {
															setDatePatternModal2(true);
														}}
													>
														<CalendarMonthOutlinedIcon />
													</Button>
												</div>
											</FormControl>
										) : ""}
									</Stack>
								</AccordionDetails>
							</Accordion>
						)}
					</>
				)}
			</Stack>
			<DatePattern
				openModal={datePatternModal}
				docPath={docPath}
				okHandler={() => {
					debounceConditionHandler("PATTERN", pattern);
					setDatePatternModal(false);
					setDpNetworkCallMessage("");
					setValid(true);
				}}
				cancelHandler={() => {
					setPattern(selectedNode?.PATTERN);
					setDatePatternModal(false);
					setDpNetworkCallMessage("");
					setValid(true);
				}}
				onChangeHandler={setPattern}
				testDateHandler={testDateHandler}
				dpNetworkCallMessage={dpNetworkCallMessage}
				datePattern={pattern}
				valid={valid}
			/>
			<DatePattern
				openModal={datePatternModal2}
				docPath={docPath}
				okHandler={() => {
					debounceConditionHandler("INBOUND_PATTERN_2", pattern2);
					setDatePatternModal2(false);
					setDpNetworkCallMessage("");
					setValid(true);
				}}
				cancelHandler={() => {
					setPattern2(selectedNode?.INBOUND_PATTERN_2);
					setDatePatternModal2(false);
					setDpNetworkCallMessage("");
					setValid(true);
				}}
				onChangeHandler={setPattern2}
				testDateHandler={testDateHandler}
				dpNetworkCallMessage={dpNetworkCallMessage}
				datePattern={pattern2}
				valid={valid}
			/>
		</Box>
	);
};

export default ConditionalIfFields;

const styles = {
	fpContainer: {
		// padding: "0 16px 16px 16px"
		padding: "0 4px 4px 4px",
	},
	accordianContainer: {
		border: "none",
		padding: "0",
		margin: "0",
		boxShadow: "none",
		backgroundColor: "unset",
	},
	accordianHeader: {
		border: "none",
		padding: "0",
		margin: "0",
		backgroundColor: "unset",
	},
	accordianContent: {
		border: "none",
		padding: "0 0px",
		margin: "0",
		backgroundColor: "unset",
	},
};
